/**
 * The swatch palette a category, tag or person can be labelled with.
 *
 * What gets stored in categories.json, tags.json and people.json is one of
 * these NAMES, never a hex. The client turns a name into
 * `var(--swatch-<name>)`, defined once per theme in `client/styles/base.css`.
 * A hex on disk would be a colour picked under whichever theme its author
 * happened to be in, frozen, and then wrong for everybody in the other one —
 * and the light theme is complete, so that is not hypothetical.
 *
 * Everything here is a `function` or a `type`, because `src/shared` forbids
 * top-level values (see `__tests__/purity.test.ts`): a `const` array would be
 * one object shared by reference between the server and the browser bundle.
 * `swatches()` therefore hands back a fresh array each call, which is also why
 * nothing may mutate what it returns and expect it to stick.
 */

export type Swatch = 'orange' | 'pink' | 'violet' | 'cyan' | 'blue' | 'lime' | 'yellow' | 'slate' | 'ink';

/** The swatches, in the order the picker shows them. */
export function swatches(): Swatch[] {
  return ['orange', 'pink', 'violet', 'cyan', 'blue', 'lime', 'yellow', 'slate', 'ink'];
}

/** What an unlabelled category/tag/person renders as. Not one of the swatches:
 *  it is the absence of a choice, not a choice, so it never appears in the
 *  picker and cannot be stored. */
export function neutralSwatch(): string {
  return 'neutral';
}

/** Narrow an unknown stored value to a swatch, or null if it is not one. On
 *  every read path, so a hand-edited registry degrades to neutral rather than
 *  emitting `var(--swatch-undefined)`. */
export function cleanSwatch(value: unknown): Swatch | null {
  if (typeof value !== 'string') return null;
  const lower = value.toLowerCase();
  return (swatches() as string[]).includes(lower) ? (lower as Swatch) : null;
}

/** The CSS custom property a swatch resolves to, with the neutral fallback
 *  baked in so callers never branch on null. */
export function swatchVar(value: Swatch | null | undefined): string {
  return `var(--swatch-${value ?? neutralSwatch()})`;
}

/** The soft/tinted companion, for a chip background behind ink. */
export function swatchSoftVar(value: Swatch | null | undefined): string {
  return `var(--swatch-${value ?? neutralSwatch()}-soft)`;
}

/** Deal swatches round-robin — for a migration that has to colour names which
 *  never had colours (the tag-order.json fold), so the result spreads across
 *  the palette rather than landing on one hue. */
export function swatchForIndex(index: number): Swatch {
  const all = swatches();
  const wrapped = ((index % all.length) + all.length) % all.length;
  // In range by construction; the index signature cannot say so under
  // noUncheckedIndexedAccess.
  return all[wrapped] ?? 'orange';
}
